import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HiringHistoryItemResponse,
  HiringHistoryResponse,
  listHiringHistory,
  peekHiringHistory,
  markHiringHistoryInterest,
  unmarkHiringHistoryInterest,
} from 'services/actions/ai_management_hiring_history';

export interface HiringHistoryItem {
  key: string;
  label: string;
  interested: boolean;
  biddings: number;
  lines: number;
  averageValue: number | null;
  medianValue: number | null;
  totalSpent: number | null;
  lastPurchaseAt: string | null;
}
interface State {
  items: HiringHistoryItem[];
  total: number;
  publicBodyName: string | null;
  loading: boolean;
  error: boolean;
}

const PER_PAGE = 10;

const toItem = (item: HiringHistoryItemResponse): HiringHistoryItem => ({
  key: item.key,
  label: item.label,
  interested: item.interesse,
  biddings: item.licitacoes,
  lines: item.linhas,
  averageValue: item.valor_medio,
  medianValue: item.valor_mediano,
  totalSpent: item.gasto_total,
  lastPurchaseAt: item.ultima_compra,
});

const fromResponse = (data: HiringHistoryResponse): State => ({
  items: data.items.map(toItem),
  total: data.total,
  publicBodyName: data.orgao?.nome ?? null,
  loading: false,
  error: false,
});

const initialState: State = {
  items: [],
  total: 0,
  publicBodyName: null,
  loading: true,
  error: false,
};

export function useHiringHistory(biddingId: number | null) {
  const [state, setState] = useState<State>(() => {
    const hit = biddingId
      ? peekHiringHistory(biddingId, { page: 1, perPage: PER_PAGE, search: '' })
      : undefined;
    return hit ? fromResponse(hit.data) : initialState;
  });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const silentRef = useRef(false);

  useEffect(() => {
    if (!biddingId) return undefined;

    let active = true;
    const silent = silentRef.current;
    silentRef.current = false;
    const params = { page, perPage: PER_PAGE, search: appliedSearch };

    const hit = peekHiringHistory(biddingId, params);
    if (hit) {
      setState(fromResponse(hit.data));
      return undefined;
    }
    if (!silent) setState((current) => ({ ...current, loading: true, error: false }));

    listHiringHistory(biddingId, params)
      .then(({ data }) => {
        if (active) setState(fromResponse(data));
      })
      .catch(() => {
        if (!active) return;
        setState({ ...initialState, loading: false, error: true });
      });

    return () => {
      active = false;
    };
  }, [biddingId, page, appliedSearch, refreshKey]);

  const submitSearch = useCallback(() => {
    setPage(1);
    setAppliedSearch(search.trim());
  }, [search]);

  const toggleInterest = useCallback(
    async (keys: string[], interested: boolean) => {
      if (!biddingId || keys.length === 0) return;

      const antes = state.items;
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          keys.includes(item.key) ? { ...item, interested } : item,
        ),
      }));

      try {
        if (interested) {
          const marcados = antes
            .filter((item) => keys.includes(item.key))
            .map((item) => ({ key: item.key, label: item.label }));
          await markHiringHistoryInterest(biddingId, marcados);
        } else {
          await unmarkHiringHistoryInterest(biddingId, keys);
        }
        silentRef.current = true;
        setRefreshKey((current) => current + 1);
      } catch {
        setState((current) => ({ ...current, items: antes }));
      }
    },
    [biddingId, state.items],
  );

  const clearSearch = useCallback(() => {
    setSearch('');
    setPage(1);
    setAppliedSearch('');
  }, []);

  return {
    ...state,
    page,
    perPage: PER_PAGE,
    search,
    appliedSearch,
    setSearch,
    submitSearch,
    clearSearch,
    setPage,
    toggleInterest,
  };
}
